/**
 * Shared renderers for equipment cards.
 * Used by PartyScreen (interactive) and InspectScreen (read-only).
 */
import { renderItemIcon } from './ItemIcon';
import { rarityColor } from './TooltipContent';
import { formatElement, formatTagShortLabel } from './GameTerms';
import type { AffixInstance, AffixDef } from '../../types';

// ─── Types ───

export interface GearCardItem {
  name: string;
  baseType: string;
  category: 'weapon' | 'armor' | 'accessory' | 'offhand';
  rarity: string;
  level: number;
}

// ─── Affix helpers ───

const NEGATIVE_PREFIX_STATS = new Set(['dot_damage_taken']);

export function formatCompactAffix(inst: AffixInstance, def: AffixDef): string {
  if (def.category === 'flat_damage') {
    const min = Math.max(1, Math.ceil(inst.roll * 0.5));
    const el = formatElement(def.element!);
    return `${min}-${inst.roll} ${el}`;
  }
  if (def.category === 'tag_increased') {
    return `+${inst.roll}% ${formatTagShortLabel(def.conditionTag ?? '')}`;
  }
  // Descale roll for affixes that use sub-integer precision storage.
  const val = def.decimals ? inst.roll / Math.pow(10, def.decimals) : inst.roll;
  const pct = def.modifier === 'percent' ? '%' : '';
  const prefix = NEGATIVE_PREFIX_STATS.has(def.stat) ? '-' : '+';
  return `${prefix}${val}${pct} ${def.name}`;
}

// ─── Renderers ───

/** Render an equipment card (weapon/armor/accessory). Matches .equip-card CSS from party.css. */
export function renderEquipCard(
  slot: string,
  label: string,
  item: GearCardItem | null,
  options?: { draggable?: boolean },
): string {
  return `
    <div class="equip-card ${item ? 'filled' : 'empty'}" data-slot="${slot}" draggable="${options?.draggable && item ? 'true' : 'false'}">
      <div class="equip-card-content">
        <div class="equip-card-label">${label}</div>
        ${item
          ? `<div class="equip-card-item-row">
               ${renderItemIcon(item.baseType ?? '', item.category, 'md')}
               <span class="equip-card-name" style="color:${rarityColor(item.rarity ?? 'normal')}">${item.name}</span>
             </div>`
          : '<span class="equip-card-empty">Empty</span>'
        }
      </div>
    </div>`;
}
